import { useCallback, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { BellIcon, CheckCheck, Inbox, Settings2 } from 'lucide-react';
import Spinner from '../Spinner';
import useClickOutside from '../../Hooks/useClickOutside';
import {
  useNotificationActions,
  useNotifications,
  useUnreadNotificationCount,
} from '../../Hooks/useNotifications';
import CategoryIcon from './CategoryIcon';
import { badgeText, fullTime, timeAgo } from './notificationMeta';

const PREVIEW_LIMIT = 8;

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
];

export const UnreadBadge = ({ count, className = '' }) =>
  count > 0 ? (
    <span
      className={`min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[18px] text-center ${className}`}
    >
      {badgeText(count)}
    </span>
  ) : null;

export const MobileNotificationLink = () => {
  const { data: unread = 0 } = useUnreadNotificationCount();
  return (
    <NavLink
      to="/notifications"
      aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
      className="relative w-8 h-8 flex items-center justify-center"
    >
      <BellIcon className="w-6 h-6 text-white" />
      <UnreadBadge count={unread} className="absolute -top-1 -right-1.5 ring-2 ring-[#1b3b5f]" />
    </NavLink>
  );
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const { data: unread = 0 } = useUnreadNotificationCount();
  const { data, isLoading, isError } = useNotifications(
    { page: 1, limit: PREVIEW_LIMIT, status: filter },
    { enabled: open }
  );
  const { markRead, markAllRead } = useNotificationActions();

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close);

  const notifications = data?.notifications ?? [];

  const openNotification = (notification) => {
    if (!notification.read) markRead.mutate(notification.id);
    setOpen(false);
    navigate(notification.link || '/notifications');
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        className="p-2 bg-[#A2A1A81A] hover:bg-gray-100 rounded-[0.625rem] transition-colors relative"
      >
        <BellIcon className="w-[1.875rem] h-[1.875rem] text-gray-600" />
        <UnreadBadge count={unread} className="absolute -top-1 -right-1 ring-2 ring-white" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[400px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <div className="flex items-center gap-2">
              <p className="text-[15px] font-semibold text-gray-900">Notifications</p>
              {unread > 0 && (
                <span className="text-[11px] font-semibold text-[#1b3b5f] bg-[#EAF0F6] px-2 py-0.5 rounded-full">
                  {unread} unread
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={unread === 0 || markAllRead.isPending}
                className="flex items-center gap-1 text-xs font-medium text-[#1b3b5f] px-2 py-1 rounded-md hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
              <NavLink
                to="/settings?tab=notifications"
                onClick={close}
                aria-label="Notification settings"
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              >
                <Settings2 size={15} />
              </NavLink>
            </div>
          </div>

          <div className="flex gap-1 px-4 pb-2 border-b border-gray-100">
            {FILTERS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                  filter === item.key
                    ? 'bg-[#1b3b5f] text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {isLoading && (
              <div className="flex justify-center py-10">
                <Spinner size="28px" />
              </div>
            )}

            {!isLoading && isError && (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                Notifications could not be loaded. Try again shortly.
              </p>
            )}

            {!isLoading && !isError && notifications.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                <Inbox className="text-gray-300" size={30} />
                <p className="text-sm font-medium text-gray-700">
                  {filter === 'unread' ? "You're all caught up" : 'No notifications yet'}
                </p>
                <p className="text-xs text-gray-400">
                  New quotes, orders, payments and sign-ups will show up here.
                </p>
              </div>
            )}

            {!isLoading &&
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => openNotification(notification)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-gray-50 last:border-b-0 transition-colors hover:bg-gray-50 ${
                    notification.read ? '' : 'bg-[#F5F8FB]'
                  }`}
                >
                  <CategoryIcon category={notification.category} size={34} />
                  <span className="flex-1 min-w-0">
                    <span
                      className={`block text-[13px] leading-snug truncate ${
                        notification.read ? 'text-gray-700' : 'text-gray-900 font-semibold'
                      }`}
                    >
                      {notification.title}
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {notification.message}
                    </span>
                    <span
                      className="block text-[11px] text-gray-400 mt-1"
                      title={fullTime(notification.created_at)}
                    >
                      {timeAgo(notification.created_at)}
                    </span>
                  </span>
                  {!notification.read && (
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-[#1b3b5f] flex-shrink-0" />
                  )}
                </button>
              ))}
          </div>

          <NavLink
            to="/notifications"
            onClick={close}
            className="block text-center text-sm font-medium text-[#1b3b5f] py-2.5 border-t border-gray-100 hover:bg-gray-50"
          >
            View all notifications
          </NavLink>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
